/**
 * Quality Tab Component
 *
 * Displays quality assessment dashboard with scores and recommendations
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  AlertCircle,
  Award,
  TrendingUp,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getLatestQualityAssessment,
  triggerQualityAssessment,
} from '../../lib/api/enterpriseSystemsApi';

interface QualityTabProps {
  systemId: string;
}

const QualityTab: React.FC<QualityTabProps> = ({ systemId }) => {
  const queryClient = useQueryClient();

  // Fetch latest quality assessment
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['quality-assessment', systemId],
    queryFn: () => getLatestQualityAssessment(systemId),
    retry: false, // Don't retry if no assessment exists yet
  });

  // Trigger quality assessment mutation
  const assessMutation = useMutation({
    mutationFn: () => triggerQualityAssessment(systemId),
    onSuccess: () => {
      toast.success('Quality assessment started!');
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['quality-assessment', systemId] });
      }, 2000);
    },
    onError: (error: any) => {
      toast.error(`Failed to start assessment: ${error.message}`);
    },
  });

  /**
   * Get quality grade color
   */
  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'B':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'C':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'D':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'F':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  /**
   * Get score color
   */
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        <span className="ml-3 text-neutral-600">Loading quality assessment...</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-12 text-center">
        <Award className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">
          No Quality Assessment Yet
        </h3>
        <p className="text-neutral-600 mb-6">
          Run a quality assessment to evaluate system completeness and data quality
        </p>
        <button
          onClick={() => assessMutation.mutate()}
          disabled={assessMutation.isPending}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {assessMutation.isPending ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Assessing...
            </>
          ) : (
            <>
              <Award className="w-5 h-5" />
              Run Quality Assessment
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Quality Assessment</h2>
          <p className="text-sm text-neutral-600 mt-1">
            Evaluation of system completeness and data quality
          </p>
        </div>
        <button
          onClick={() => assessMutation.mutate()}
          disabled={assessMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors disabled:opacity-50"
        >
          {assessMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Assessing...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4" />
              Re-assess
            </>
          )}
        </button>
      </div>

      {/* Overall Score Card */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg p-8 mb-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-primary-100 text-sm font-medium mb-2">Overall Quality Score</p>
            <p className={`text-6xl font-bold`}>{Math.round(data.overall_score)}</p>
            <p className="text-primary-100 mt-2">
              Last assessed: {new Date(data.created_at).toLocaleDateString()}
            </p>
          </div>
          <div
            className={`px-8 py-4 ${getGradeColor(
              data.overall_grade
            )} rounded-lg border-2 text-center`}
          >
            <p className="text-sm font-medium mb-1">Grade</p>
            <p className="text-5xl font-bold">{data.overall_grade}</p>
          </div>
        </div>
      </div>

      {/* Dimension Scores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {data.dimension_scores?.map((dimension) => (
          <div
            key={dimension.dimension}
            className="bg-white border border-neutral-200 rounded-lg p-6"
          >
            <h3 className="text-sm font-medium text-neutral-900 mb-3">
              {dimension.dimension
                .split('_')
                .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ')}
            </h3>
            <div className="flex items-baseline gap-2 mb-3">
              <p className={`text-4xl font-bold ${getScoreColor(dimension.score)}`}>
                {Math.round(dimension.score)}
              </p>
              <p className="text-sm text-neutral-500">/ 100</p>
            </div>
            <div className="w-full bg-neutral-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  dimension.score >= 80
                    ? 'bg-green-500'
                    : dimension.score >= 60
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }`}
                style={{ width: `${dimension.score}%` }}
              />
            </div>
            {dimension.details && (
              <p className="text-xs text-neutral-600 mt-3 line-clamp-2">
                {dimension.details}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Priority Recommendations */}
      {data.priority_recommendations && data.priority_recommendations.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-neutral-900 mb-4">Priority Recommendations</h3>
          <div className="space-y-3">
            {data.priority_recommendations.map((rec: string, index: number) => (
              <div
                key={index}
                className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r-lg"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-neutral-700">{rec}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issues Summary */}
      {data.issues && data.issues.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-neutral-900 mb-4">Issues Found</h3>
          <div className="space-y-3">
            {data.issues.slice(0, 5).map((issue) => (
              <div
                key={issue.issue_id}
                className={`border-l-4 p-4 rounded-r-lg ${
                  issue.severity === 'critical'
                    ? 'border-red-500 bg-red-50'
                    : issue.severity === 'high'
                    ? 'border-orange-500 bg-orange-50'
                    : issue.severity === 'medium'
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-blue-500 bg-blue-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                    issue.severity === 'critical' ? 'text-red-600' :
                    issue.severity === 'high' ? 'text-orange-600' :
                    issue.severity === 'medium' ? 'text-yellow-600' : 'text-blue-600'
                  }`} />
                  <div className="flex-1">
                    <p className="font-medium text-neutral-900 mb-1">{issue.title}</p>
                    <p className="text-sm text-neutral-700">{issue.description}</p>
                    {issue.recommendation && (
                      <p className="text-xs text-neutral-600 mt-2">
                        <span className="font-medium">Recommendation:</span> {issue.recommendation}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {data.issues.length > 5 && (
              <p className="text-sm text-neutral-500 text-center">
                +{data.issues.length - 5} more issues
              </p>
            )}
          </div>
        </div>
      )}

      {/* Improvement & Regression Areas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Improvement Areas */}
        {data.improvement_areas && data.improvement_areas.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-green-900">Improvement Areas</h3>
            </div>
            <ul className="space-y-2">
              {data.improvement_areas.map((area: string, index: number) => (
                <li key={index} className="flex items-start gap-2 text-sm text-green-800">
                  <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{area}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Regression Areas */}
        {data.regression_areas && data.regression_areas.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold text-red-900">Regression Areas</h3>
            </div>
            <ul className="space-y-2">
              {data.regression_areas.map((area: string, index: number) => (
                <li key={index} className="flex items-start gap-2 text-sm text-red-800">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{area}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default QualityTab;
